import { createSelector } from 'reselect';

// Types
import { RootState } from 'types/RootState.types';

// Config
import { PAYMENT_BLOCKERS } from '@components/payment-option-unavailable/PaymentOptionUnavailable.config';
import featureConstants from '@constants/featureConstants';
import { KLARNA_MAX_SPEND, KLARNA_MIN_SPEND } from '@constants/paymentConstants';
import { isFeatureActive } from '@redux/reducers/config/configReducer';
import { getDeliveriesWithClearabee } from '@redux/reducers/delivery/deliverySelector';
import {
  getOutstandingBalanceRawSelector,
  orderFormBasketContainsAlcoholSelector,
} from '@redux/reducers/order-form/orderFormSelector';

export const shouldShowKlarnaFeature = (state: RootState): boolean =>
  !isFeatureActive(state, featureConstants.DISABLE_KLARNA);

export type KlarnaEligibilityType = {
  isEligible: boolean;
  isEnabled: boolean;
  reasons: (typeof PAYMENT_BLOCKERS)[keyof typeof PAYMENT_BLOCKERS][];
};

export const getBasketContainsAlcoholSelector = (state: RootState) => orderFormBasketContainsAlcoholSelector(state);

export const selectKlarnaIneligibilityReasons = createSelector(
  [getDeliveriesWithClearabee, getOutstandingBalanceRawSelector, getBasketContainsAlcoholSelector],
  (deliveriesWithClearabee = [], outstandingBalanceRaw, basketContainsAlcohol) => {
    const reasons: (typeof PAYMENT_BLOCKERS)[keyof typeof PAYMENT_BLOCKERS][] = [];

    if (basketContainsAlcohol) {
      reasons.push(PAYMENT_BLOCKERS.BASKET_CONTAINS_ALCOHOL);
    }
    if (deliveriesWithClearabee.length > 0) {
      reasons.push(PAYMENT_BLOCKERS.CLEARABEE_DELIVERIES);
    }
    if (Number(outstandingBalanceRaw) < KLARNA_MIN_SPEND) {
      reasons.push(PAYMENT_BLOCKERS.BALANCE_BELOW_MIN);
    }
    if (Number(outstandingBalanceRaw) > KLARNA_MAX_SPEND) {
      reasons.push(PAYMENT_BLOCKERS.BALANCE_ABOVE_MAX);
    }

    return reasons;
  },
);

export const isBasketKlarnaEligible = createSelector(
  [shouldShowKlarnaFeature, selectKlarnaIneligibilityReasons],
  (shouldShowKlarnaFeature, reasons) => ({
    isEligible: reasons.length === 0,
    isEnabled: shouldShowKlarnaFeature,
    reasons,
  }),
);
